import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusPill from "@/components/StatusPill";
import { MagnifyingGlass, QrCode, Camera, X, ClipboardText, CheckCircle, Warning } from "@phosphor-icons/react";
import { useQrScanner } from "@/hooks/useQrScanner";
import { useAuth } from "@/context/AuthContext";
import { devDebug } from "@/lib/debug";
import { playSuccessBeep, playAlarmBeep, playInfoBeep } from "@/lib/scanSounds";
import { compressImage } from "@/lib/imageCompress";

const SCANNER_EL_ID = "qr-scanner-region";

export default function LoadingOps() {
  const { user } = useAuth();
  const isPetugas = user?.role === "petugas";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [queue, setQueue] = useState([]);   // belum_termuat SPBs (auto-loaded for petugas)
  const [selectedId, setSelectedId] = useState(null);
  const [jumlahInput, setJumlahInput] = useState("");
  const [uploading, setUploading] = useState(null);
  const [uploadPhase, setUploadPhase] = useState(null); // "compress" | "upload" | null
  const [uploadPct, setUploadPct] = useState(0);
  // verifiedSpb: shows fullscreen success card for 3s after scan-verify
  const [verifiedSpb, setVerifiedSpb] = useState(null);

  const loadQueue = async () => {
    try {
      const { data } = await api.get("/spb", { params: { status: "belum_termuat", limit: 100 } });
      setQueue(data);
    } catch (err) {
      devDebug("queue load failed", err);
    }
  };

  useEffect(() => { loadQueue(); }, []);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const { data } = await api.get("/spb", { params: { q: q.trim() } });
      setResults(data);
      if (data.length === 0) toast.info("Tidak ada SPB yang cocok");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDecode = async (decoded) => {
    const qr = (decoded || "").trim();
    if (!qr) return;
    // Fall back to plain search for admin/kantor (they can't scan-verify).
    // Only petugas gets the smart-dispatch scan endpoint.
    if (!isPetugas) {
      const m = qr.match(/SPB:([^|]+)/);
      const noSpb = m ? m[1].trim() : qr;
      setQ(noSpb);
      try {
        const { data } = await api.get(`/spb/lookup/${encodeURIComponent(noSpb)}`);
        setResults([data]);
        toast.success(`Ditemukan SPB ${data.no_spb}`);
      } catch (err) {
        const { data } = await api.get("/spb", { params: { q: noSpb } });
        setResults(data);
      }
      return;
    }

    // Petugas path — server does the smart-dispatch + anti-fraud checks
    try {
      const { data } = await api.post("/spb/scan", { qr_content: qr });
      if (data.action === "verify") {
        playSuccessBeep();
        setVerifiedSpb(data.spb);
        setResults([]);
        setSelectedId(null);
        // Auto-reset after 3s so next truck can be scanned
        setTimeout(() => {
          setVerifiedSpb(null);
          loadQueue();
        }, 3000);
      } else if (data.action === "load") {
        // belum_termuat — show muat form
        playInfoBeep();
        setResults([data.spb]);
        setSelectedId(data.spb.id);
        setJumlahInput(String(data.spb.jumlah || ""));
        toast.info(`SPB ${data.spb.no_spb} — silakan isi jumlah termuat & foto bukti`);
      } else if (data.action === "already_verified") {
        playInfoBeep();
        toast.info(
          `SPB ${data.spb.no_spb} sudah diverifikasi oleh ${data.spb.diverifikasi_by_name || "petugas"}`,
          { duration: 5000 },
        );
      }
    } catch (err) {
      playAlarmBeep();
      const msg = formatApiError(err);
      toast.error(msg, { duration: 8000, icon: "⚠️" });
    }
  };

  const { scanning, start: startScan, stop: stopScan } = useQrScanner({
    elementId: SCANNER_EL_ID,
    onDecode,
    onError: () => toast.error("Tidak bisa akses kamera"),
  });

  const onConfirmMuat = async (spb, file) => {
    if (!file) {
      toast.error("Foto bukti muat wajib diupload");
      return;
    }
    if (!jumlahInput || parseFloat(jumlahInput) <= 0) {
      toast.error("Isi jumlah termuat dulu");
      return;
    }
    setUploading(spb.id);
    setUploadPhase("compress");
    setUploadPct(0);
    const compressed = await compressImage(file);
    const fd = new FormData();
    fd.append("file", compressed);
    fd.append("jumlah_termuat", jumlahInput);
    setUploadPhase("upload");
    try {
      await api.post(`/spb/${spb.id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success(`SPB ${spb.no_spb} berhasil dimuat (${jumlahInput})`);
      setSelectedId(null);
      setJumlahInput("");
      loadQueue();
      if (results.length) search();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
      setUploadPhase(null);
      setUploadPct(0);
    }
  };

  const onSelectQueue = (spb) => {
    setSelectedId((prev) => prev === spb.id ? null : spb.id);
    setJumlahInput(String(spb.jumlah || ""));
  };

  const renderLoadForm = (spb) => (
    <div className="mt-3 border-t border-primary/30 pt-3 space-y-3" data-testid={`muat-form-${spb.no_spb}`}>
      <div>
        <Label className="overline block mb-1">Jumlah Termuat (aktual)</Label>
        <Input
          type="number" step="0.01" min="0"
          value={jumlahInput}
          onChange={(e) => setJumlahInput(e.target.value)}
          className="rounded-none h-11 font-mono text-lg"
          placeholder={`Contoh: ${spb.jumlah}`}
          data-testid={`input-jumlah-termuat-${spb.no_spb}`}
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          SPB minta {spb.jumlah} {spb.satuan}. Isi jumlah aktual yang benar-benar dimuat.
        </p>
      </div>
      <label className={`block ${uploading === spb.id ? "cursor-not-allowed" : "cursor-pointer"}`}>
        <input
          type="file" accept="image/*"
          onChange={(e) => onConfirmMuat(spb, e.target.files?.[0])}
          className="hidden"
          disabled={uploading === spb.id}
          data-testid={`confirm-muat-${spb.no_spb}`}
        />
        <span className={`relative overflow-hidden w-full inline-flex items-center justify-center gap-2 px-4 h-12 border-2 font-bold uppercase tracking-wider text-sm ${uploading === spb.id ? "bg-secondary border-border" : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"}`}>
          {uploading === spb.id && uploadPhase === "upload" && (
            <span
              className="absolute inset-y-0 left-0 bg-primary/25 transition-[width] duration-200 ease-out"
              style={{ width: `${uploadPct}%` }}
              data-testid={`upload-progress-bar-${spb.no_spb}`}
              aria-hidden="true"
            />
          )}
          <Camera size={18} weight="bold" className="relative z-10" />
          <span className="relative z-10" data-testid={uploading === spb.id ? `upload-progress-label-${spb.no_spb}` : undefined}>
            {uploading !== spb.id && "Foto / Galeri — Konfirmasi Muat"}
            {uploading === spb.id && uploadPhase === "compress" && "Mengompres foto..."}
            {uploading === spb.id && uploadPhase === "upload" && `Mengupload ${uploadPct}%`}
          </span>
        </span>
      </label>
    </div>
  );

  const scanBlock = (
    <div className={`space-y-3 mb-6 ${isPetugas ? "" : "border-t border-border pt-6"}`} data-testid="scan-block">
      <div className="overline mb-1">Scan / Cari Manual</div>
      {!scanning ? (
        <Button
          onClick={startScan}
          className="w-full rounded-none h-14 font-bold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="scan-qr-button"
        >
          <QrCode size={22} weight="bold" className="mr-2" /> Scan QR Code
        </Button>
      ) : (
        <div className="border-2 border-primary bg-card">
          <div className="p-3 flex justify-between items-center border-b border-border">
            <div className="overline">Scanning...</div>
            <button onClick={stopScan} className="text-sm font-semibold flex items-center gap-1" data-testid="stop-scan-button">
              <X size={16} /> Tutup
            </button>
          </div>
          <div id={SCANNER_EL_ID} className="min-h-[280px]" />
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Ketik no SPB / no polisi"
          className="rounded-none h-12 text-base"
          data-testid="ops-search-input"
        />
        <Button onClick={search} variant="outline" className="rounded-none h-12 min-w-14" data-testid="ops-search-button">
          <MagnifyingGlass size={20} weight="bold" />
        </Button>
      </div>
    </div>
  );

  const queueBlock = (
    <div className="mb-6" data-testid="loading-queue">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardText size={18} weight="bold" />
          <h2 className="font-bold uppercase tracking-wider text-sm">Antrian Belum Termuat ({queue.length})</h2>
        </div>
        {queue.length === 0 ? (
          <div className="border border-dashed border-border p-6 text-center text-muted-foreground text-sm">
            Tidak ada SPB belum termuat. Semua SPB sudah dimuat 🎉
          </div>
        ) : (
          <div className="space-y-2">
            {queue.map((spb) => {
              const isOpen = selectedId === spb.id;
              return (
                <div key={spb.id} className={`border ${isOpen ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`queue-row-${spb.no_spb}`}>
                  <button
                    type="button"
                    onClick={() => onSelectQueue(spb)}
                    className="w-full text-left p-3 flex items-center gap-3 hover:bg-secondary/40"
                    data-testid={`queue-select-${spb.no_spb}`}
                  >
                    <div className={`w-5 h-5 border-2 ${isOpen ? "bg-primary border-primary" : "border-border"} flex items-center justify-center flex-shrink-0`}>
                      {isOpen && <CheckCircle size={14} weight="fill" className="text-primary-foreground" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-mono font-black text-base flex items-center gap-2 flex-wrap">
                        {spb.no_spb}
                        <span className="text-muted-foreground font-bold text-sm">{spb.no_polisi}</span>
                        {spb.slot_antrian && (
                          <span
                            className="text-[10px] font-mono font-bold bg-primary/10 text-primary border border-primary/40 px-1.5 py-0.5"
                            data-testid={`queue-slot-${spb.no_spb}`}
                          >
                            🕒 {spb.slot_antrian}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        {spb.produk_nama} · {spb.jumlah} {spb.satuan} · {spb.distrik_kode}
                      </div>
                    </div>
                    <Link to={`/spb/${spb.id}`} onClick={(e) => e.stopPropagation()} className="text-xs font-semibold hover:underline">
                      Detail
                    </Link>
                  </button>
                  {isOpen && <div className="px-3 pb-3">{renderLoadForm(spb)}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      {/* Fullscreen SUCCESS overlay — auto-dismissed after 3s */}
      {verifiedSpb && (
        <div
          className="fixed inset-0 z-50 bg-green-600/95 flex items-center justify-center p-6 animate-in fade-in"
          data-testid="scan-verify-success-overlay"
        >
          <div className="max-w-md w-full text-white text-center space-y-4">
            <CheckCircle size={120} weight="fill" className="mx-auto animate-in zoom-in-50" />
            <div className="overline text-white/90">SPB Terverifikasi</div>
            <div className="text-4xl font-black font-mono">{verifiedSpb.no_spb}</div>
            <div className="text-lg font-mono opacity-90">
              {verifiedSpb.no_polisi} · {verifiedSpb.produk_nama}
            </div>
            <div className="text-2xl font-black">
              {verifiedSpb.jumlah_termuat || verifiedSpb.jumlah} {verifiedSpb.satuan}
            </div>
            {verifiedSpb.nama_pelanggan && (
              <div className="text-sm font-mono opacity-90">
                → {verifiedSpb.nama_pelanggan}
              </div>
            )}
            <div className="text-xs opacity-75 mt-6">Layar akan reset dalam 3 detik...</div>
          </div>
        </div>
      )}

      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Cek Validasi</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Cek Status Truk</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {isPetugas
            ? "Scan QR paper SPB. Kalau belum termuat → isi jumlah + foto. Kalau sudah termuat → langsung diverifikasi. QR palsu / tidak match akan berbunyi alarm."
            : "Pilih SPB dari antrian, isi jumlah termuat, lalu foto bukti muat."}
        </p>
      </div>

      {isPetugas ? (
        <>
          {scanBlock}
          {queueBlock}
        </>
      ) : (
        <>
          {queueBlock}
          {scanBlock}
        </>
      )}

      {/* Search Results */}
      <div className="space-y-4">
        {results.map((spb) => {
          const isOpen = selectedId === spb.id;
          return (
            <div key={spb.id} className="border border-border bg-card p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="overline">SPB</div>
                  <Link to={`/spb/${spb.id}`} className="text-xl font-black font-mono tracking-tight hover:underline">
                    {spb.no_spb}
                  </Link>
                </div>
                <StatusPill status={spb.status} />
              </div>
              <div className="text-sm space-y-1 mb-4 font-mono">
                <div><span className="overline mr-2">Polisi</span><strong>{spb.no_polisi}</strong></div>
                <div><span className="overline mr-2">Supir</span>{spb.nama_supir || "-"}</div>
                <div><span className="overline mr-2">Produk</span>{spb.produk_nama}</div>
                <div><span className="overline mr-2">Jumlah</span>{spb.jumlah} {spb.satuan}</div>
                <div><span className="overline mr-2">Distrik</span>{spb.distrik_kode} ({spb.distrik_nama})</div>
                {spb.nama_pelanggan && (
                  <div data-testid={`loading-tujuan-${spb.no_spb}`}>
                    <span className="overline mr-2">Tujuan</span>
                    <span className="whitespace-pre-line">{spb.nama_pelanggan}{spb.alamat_pengiriman ? ` — ${spb.alamat_pengiriman}` : ""}</span>
                  </div>
                )}
              </div>
              {spb.status === "belum_termuat" ? (
                <>
                  <Button
                    onClick={() => onSelectQueue(spb)}
                    className="w-full rounded-none h-11 font-bold uppercase tracking-wider"
                    data-testid={`result-select-${spb.no_spb}`}
                  >
                    {isOpen ? "Batal" : "Muat SPB Ini"}
                  </Button>
                  {isOpen && renderLoadForm(spb)}
                </>
              ) : (
                <Link to={`/spb/${spb.id}`} className="inline-flex items-center px-4 border border-border font-semibold text-sm h-12">
                  Detail
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
